import { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import type { JobRoleSapSyncSummary } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useSapRoleSync } from '../../hooks/useRoles';
import { Card, CardHeader, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';

/**
 * The "Sync with SAP" card on the Job Roles admin page. One click pulls
 * the SAP job list and labour resources and upserts the roles list; SAP
 * itself is only ever read. The last run's summary stays on screen so the
 * admin can act on conflicts and missing jobs.
 */
export function SapRoleSyncCard() {
  const sync = useSapRoleSync();
  const [summary, setSummary] = useState<JobRoleSapSyncSummary | null>(null);

  async function onSync() {
    try {
      const res = await sync.mutateAsync();
      setSummary(res.summary);
      toast.success(`Synced ${res.summary.jobs_seen} SAP jobs`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'SAP sync failed');
    }
  }

  return (
    <Card>
      <CardHeader
        title="Sync with SAP"
        trailing={
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<RefreshCw className="h-4 w-4" />}
            onClick={onSync}
            loading={sync.isPending}
          >
            Sync with SAP
          </Button>
        }
      />
      <CardSection>
        <p className="text-sm text-fg-muted">
          Pulls the job list and labour resources from SAP ByDesign and updates this
          list. SAP is read-only: jobs are created and renamed in SAP, never from here.
          Roles that only exist here are left alone and flagged.
        </p>
        {summary ? <SyncSummary summary={summary} /> : null}
      </CardSection>
    </Card>
  );
}

function SyncSummary({ summary }: { summary: JobRoleSapSyncSummary }) {
  return (
    <div className="mt-3 space-y-2 text-sm">
      <div className="flex flex-wrap gap-1">
        <Pill tone="neutral" size="sm">{summary.jobs_seen} jobs</Pill>
        <Pill tone="neutral" size="sm">{summary.resources_seen} resources</Pill>
        {summary.created.length > 0 ? (
          <Pill tone="success" size="sm">{summary.created.length} created</Pill>
        ) : null}
        {summary.adopted.length > 0 ? (
          <Pill tone="success" size="sm">{summary.adopted.length} adopted</Pill>
        ) : null}
        {summary.renamed.length > 0 ? (
          <Pill tone="info" size="sm">{summary.renamed.length} renamed</Pill>
        ) : null}
        {summary.resources_changed > 0 ? (
          <Pill tone="info" size="sm">{summary.resources_changed} resource changes</Pill>
        ) : null}
        {summary.no_tariff > 0 ? (
          <Pill tone="warning" size="sm">{summary.no_tariff} without tariff</Pill>
        ) : null}
        {summary.missing_from_sap.length > 0 ? (
          <Pill tone="danger" size="sm">{summary.missing_from_sap.length} removed from SAP</Pill>
        ) : null}
        {summary.conflicts.length > 0 ? (
          <Pill tone="danger" size="sm">{summary.conflicts.length} conflicts</Pill>
        ) : null}
      </div>
      {summary.renamed.length > 0 ? (
        <ul className="list-inside list-disc text-xs text-fg-muted">
          {summary.renamed.map((r) => (
            <li key={r.to}>
              renamed "{r.from}" to "{r.to}"
            </li>
          ))}
        </ul>
      ) : null}
      {summary.missing_from_sap.length > 0 ? (
        <p className="text-xs text-fg-muted">
          No longer in SAP: {summary.missing_from_sap.join(', ')}. Archive them here if
          they are finished with.
        </p>
      ) : null}
      {summary.conflicts.length > 0 ? (
        <ul className="list-inside list-disc text-xs text-danger">
          {summary.conflicts.map((c) => (
            <li key={c.job_id}>
              SAP job {c.job_id} "{c.name}": {c.reason}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
